import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { Bell, X, Check, Loader2, Phone, Calendar, AlertTriangle, Trash2, Megaphone, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const ICON_MAP = {
  appointment: Calendar,
  followup_overdue: AlertTriangle,
  new_call: Phone,
  campaign_low_answer_rate: Megaphone,
  proactive_prompt_suggestion: Sparkles,
};

export const EliosNotificationBell = ({ apiPrefix = '/elios' }) => {
  const [open, setOpen] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleNotifClick = useCallback(async (n) => {
    // Mark as read on click (best-effort, fire-and-forget)
    if (!n.read && n.id) {
      api.post(`${apiPrefix}/notifications/read`, { notification_ids: [n.id] }).catch(() => {});
      setNotifs(prev => prev.map(x => x.id === n.id ? { ...x, read: true } : x));
      setUnread(prev => Math.max(0, prev - 1));
    }
    // Follow deeplink if present in payload
    const link = n?.data?.deeplink;
    if (link) {
      setOpen(false);
      navigate(link);
    }
  }, [apiPrefix, navigate]);

  // Real-time push: when a new notification arrives, show a toast.
  // We track the set of already-seen ids to only toast genuinely new ones.
  const seenIdsRef = useRef(new Set());
  const firstLoadRef = useRef(true);

  const fetchNotifs = useCallback(async () => {
    try {
      const r = await api.get(`${apiPrefix}/notifications?limit=30`);
      const list = r.data.notifications || [];
      setNotifs(list);
      setUnread(r.data.unread_count || 0);

      // On first load: seed the seen-set without toasting (baseline).
      // Afterwards: every fresh unread id triggers a toast.
      if (firstLoadRef.current) {
        list.forEach(n => seenIdsRef.current.add(n.id));
        firstLoadRef.current = false;
        return;
      }
      const fresh = list.filter(n => n.id && !n.read && !seenIdsRef.current.has(n.id));
      fresh.forEach(n => {
        seenIdsRef.current.add(n.id);
        const icon = ICON_MAP[n.type] || Bell;
        const isAiAppt = n.type === 'appointment'
          && (n.title?.toLowerCase().includes('agente vocale') || n.title?.toLowerCase().includes('ai'));

        if (isAiAppt) {
          toast.success(
            `Nuovo appuntamento AI: ${n.message || n.title}`,
            {
              icon: <Sparkles className="w-4 h-4 text-violet-500" />,
              duration: 8000,
              description: n.title,
              action: {
                label: 'Apri',
                onClick: () => {
                  window.location.href = '/agenda?filter=ai&period=upcoming';
                },
              },
            }
          );
        } else if (n.type === 'new_call') {
          toast.info(n.title || 'Nuova chiamata', {
            description: n.message,
            duration: 5000,
          });
        } else {
          toast(n.title || 'Notifica', {
            description: n.message,
            duration: 5000,
          });
        }
        // Optional subtle sound for AI appointment (browser beep via WebAudio).
        if (isAiAppt) {
          try {
            const ctx = new (window.AudioContext || window.webkitAudioContext)();
            const o = ctx.createOscillator();
            const g = ctx.createGain();
            o.connect(g); g.connect(ctx.destination);
            o.type = 'sine'; o.frequency.setValueAtTime(880, ctx.currentTime);
            g.gain.setValueAtTime(0.05, ctx.currentTime);
            g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.3);
            o.start(); o.stop(ctx.currentTime + 0.3);
          } catch { /* no audio available, silent */ }
        }
      });
    } catch { /* ignore */ }
  }, [apiPrefix]);

  useEffect(() => {
    fetchNotifs();
    // Poll every 30s so new notifications appear within at most 30s
    const t = setInterval(fetchNotifs, 30000);
    return () => clearInterval(t);
  }, [fetchNotifs]);

  const markAllRead = async () => {
    setLoading(true);
    try {
      await api.post(`${apiPrefix}/notifications/read`, { mark_all: true });
      setUnread(0);
      setNotifs(n => n.map(x => ({ ...x, read: true })));
    } catch {}
    setLoading(false);
  };

  const clearAll = async () => {
    setLoading(true);
    try {
      await api.delete(`${apiPrefix}/notifications`);
      setNotifs([]);
      setUnread(0);
    } catch {}
    setLoading(false);
  };

  return (
    <div className="relative" data-testid="notification-bell">
      <button onClick={() => setOpen(!open)} className="relative p-2 rounded-lg hover:bg-gray-100 transition-colors" data-testid="notification-bell-btn">
        <Bell className="w-5 h-5 text-gray-600" />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">{unread > 9 ? '9+' : unread}</span>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-2 w-80 sm:w-96 bg-white rounded-xl shadow-2xl border border-gray-200 z-50 max-h-[70vh] flex flex-col overflow-hidden" style={{ right: '0', minWidth: '320px' }} data-testid="notification-panel">
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <h3 className="font-semibold text-sm text-gray-900">Notifiche</h3>
              <div className="flex items-center gap-1">
                {notifs.length > 0 && (
                  <Button variant="ghost" size="sm" className="text-xs h-7 gap-1 text-red-500" onClick={clearAll} disabled={loading} data-testid="clear-notifs-btn">
                    {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />} Cancella
                  </Button>
                )}
                {unread > 0 && (
                  <Button variant="ghost" size="sm" className="text-xs h-7 gap-1 text-indigo-600" onClick={markAllRead} disabled={loading}>
                    {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />} Segna tutte
                  </Button>
                )}
                <button onClick={() => setOpen(false)} className="text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>
              </div>
            </div>
            <div className="overflow-y-auto flex-1">
              {notifs.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-8">Nessuna notifica</p>
              ) : (
                notifs.map((n, i) => {
                  const Icon = ICON_MAP[n.type] || Bell;
                  const hasLink = !!n?.data?.deeplink;
                  const Wrapper = hasLink ? 'button' : 'div';
                  return (
                    <Wrapper
                      key={n.id || i}
                      onClick={hasLink ? () => handleNotifClick(n) : undefined}
                      className={`flex gap-3 px-4 py-3 border-b border-gray-50 w-full text-left ${!n.read ? 'bg-indigo-50/50' : ''} ${hasLink ? 'hover:bg-indigo-50 cursor-pointer transition-colors' : ''}`}
                      data-testid={`notif-item-${n.id || i}`}
                    >
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${!n.read ? 'bg-indigo-100 text-indigo-600' : 'bg-gray-100 text-gray-400'}`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-xs font-medium ${!n.read ? 'text-gray-900' : 'text-gray-600'}`}>{n.title}</p>
                        <p className="text-[11px] text-gray-500 line-clamp-2">{n.message}</p>
                        <p className="text-[10px] text-gray-400 mt-0.5">{n.created_at ? new Date(n.created_at).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}</p>
                      </div>
                    </Wrapper>
                  );
                })
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};
